import React, { useEffect, useRef, useState } from 'react';

import { useSelector } from 'react-redux';

import RightSidebar from '@components/RightSidebar/RightSidebar';
import Input from '@uikit/Form/Input/Input';
import Select from '@uikit/Form/Select/Select';
import Loader from '@uikit/old/Loader/Loader';

import './EditWorkspaceSidebar.scss';
import { WorkspaceType } from '@ts/userOrganization.types';
import AutomationPositionTooltipContent from '@components/CampaignSettings/ContactAutomationSetup/_components/AutomationPositionTooltipContent/AutomationPositionTooltipContent';
import { billingAppDetailsSelector } from '@redux/selectors/billings-app.selectors';
import { QueryPageHookType } from '@ts/common.types';
import { MailboxAccountStatisticsType } from '@ts/mailboxAccounts.types';
import useOrganizationMembers from '@hooks/useOrganizationMembers';
import { isSemrushMode, isStandaloneMode } from '@constants/app-modes';
import { isAppRealm } from '@utils/realmIdentifier';
import useMailboxAccountStatistics from '@hooks/useMailboxAccountStatistics';
import { FilterFieldType } from '@ts/filterField.type';
import {
  MailboxAccountFilterField,
  MailboxAccountFilterFieldMap,
} from 'respona_api/generated/mailbox-account_pb';
import debounce from '@utils/debounce';

function EditWorkspaceSidebar({
  workspace,
  isOpen,
  onClose,
  onUpdate,
}: {
  workspace: WorkspaceType;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (
    workspaceId: number,
    title: string,
    logo: string,
    members: number[],
    mailboxes: number[]
  ) => void;
}): JSX.Element {
  if (!isOpen || !workspace) {
    return <div />;
  }

  const {
    data: { enterpriseFeaturesEnabled, extendedFeaturesEnabled },
  } = useSelector(billingAppDetailsSelector);

  const [mailboxFilters, setMailboxFilters] = useState<
    FilterFieldType<MailboxAccountFilterFieldMap>[]
  >([]);

  const {
    items: baseAccounts,
    isLoading: isMailboxLoading,
    isLoadingNextPage: isLoadingNextPageMailboxAccounts,
    hasNextPage: baseAccountsHasNextPage,
    fetchNextPage: fetchNextBaseAccountPage,
  }: QueryPageHookType<MailboxAccountStatisticsType> = useMailboxAccountStatistics(mailboxFilters);

  const mailboxSearchDebounced = useRef(
    debounce((value: string) => {
      const trimmed = (value || '').trim();
      if (trimmed.length === 0) {
        setMailboxFilters([]);
        return;
      }
      const emailFilter: FilterFieldType<MailboxAccountFilterFieldMap> = {
        field: MailboxAccountFilterField.STRING_EMAIL,
        join: 0,
        operation: 2,
        order: 0,
        value: trimmed,
      };
      setMailboxFilters([emailFilter]);
    }, 400)
  );

  const [members, changeMembers] = useState<{ label: string; value: number }[]>(
    workspace.membersList.map((item) => {
      return { label: item.fullName, value: item.userId };
    })
  );
  const [mailboxes, changeMailboxes] = useState<{ label: string; value: number }[]>(
    workspace.accountsList.map((item) => {
      return { label: item.email, value: item.id };
    })
  );
  const [workspaceTitle, changeWorkspaceTitle] = useState(workspace.title);

  useEffect(() => {
    changeWorkspaceTitle(workspace.title);
    changeMailboxes(
      workspace.accountsList.map((item) => {
        return { label: item.email, value: item.id };
      })
    );
    changeMembers(
      workspace.membersList.map((item) => {
        return { label: item.fullName, value: item.userId };
      })
    );
  }, [workspace.id]);

  const { items: organizationMembers, isLoading: isMembersLoading } = useOrganizationMembers(true);

  const handleChangeWorkspaceTitle = ({ target: { value } }) => changeWorkspaceTitle(value);
  const handleSave = () =>
    onUpdate(
      workspace.id,
      workspaceTitle,
      workspace.logoUrl,
      members.map((item) => item.value),
      mailboxes.map((item) => item.value)
    );

  function composeSidebarTitle() {
    return `Edit workspace \n${workspace.title}`;
  }

  return (
    <RightSidebar
      isOpen={isOpen}
      title={composeSidebarTitle()}
      onClose={onClose}
      onSave={handleSave}
      width="350px"
    >
      <div className="edit-workspace-sidebar">
        <div className="edit-workspace-sidebar__row">
          <div className="edit-workspace-sidebar__field-label">Workspace name</div>
          <div className="edit-workspace-sidebar__field">
            <Input isFullWidth value={workspaceTitle} onChange={handleChangeWorkspaceTitle} />
          </div>
        </div>

        {isStandaloneMode() && (
          <Loader isLoading={organizationMembers == null || isMembersLoading}>
            <div className="edit-workspace-sidebar__row">
              <div className="edit-workspace-sidebar__field-label">Team members</div>
              <div className="edit-workspace-sidebar__field">
                <Select
                  isMulti
                  renderMultipleValuesTooltipContent={AutomationPositionTooltipContent}
                  fieldToRender="label"
                  value={members}
                  options={
                    organizationMembers?.map((item) => {
                      return { label: item.fullName, value: item.userId };
                    }) || []
                  }
                  onChange={changeMembers}
                />
              </div>
            </div>
          </Loader>
        )}

        {isAppRealm() && (
          <div className="edit-workspace-sidebar__row">
            <div className="edit-workspace-sidebar__field-label">Email accounts</div>
            <div className="edit-workspace-sidebar__field">
              <Select
                isDisabled={
                  !isSemrushMode() &&
                  enterpriseFeaturesEnabled === false &&
                  extendedFeaturesEnabled === false
                }
                isMulti
                renderMultipleValuesTooltipContent={AutomationPositionTooltipContent}
                fieldToRender="label"
                value={mailboxes}
                options={baseAccounts?.map((item) => {
                  return { label: item.email, value: item.id };
                })}
                onChange={changeMailboxes}
                onInputChange={(value: string) => mailboxSearchDebounced.current(value)}
                onMenuScrollToBottom={() => {
                  if (baseAccountsHasNextPage && !isLoadingNextPageMailboxAccounts) {
                    fetchNextBaseAccountPage();
                  }
                }}
              />
            </div>
          </div>
        )}
      </div>
    </RightSidebar>
  );
}

export default EditWorkspaceSidebar;
